import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const pressureLevels = [
  {
    level: "Low Pressure",
    range: "Same as ordinary water sprinklers",
    use: "Small fires in homes, office spaces, warehouses and smaller enclosures",
    icon: "ri-drop-line",
    colorClass: "bg-accent-50 border-accent-200/70",
    iconClass: "bg-accent-100 text-accent-600",
  },
  {
    level: "Medium Pressure",
    range: "175 psi to 500 psi",
    use: "Wide range of fires in living and work spaces",
    icon: "ri-water-flash-line",
    colorClass: "bg-primary-50 border-primary-200/70",
    iconClass: "bg-primary-100 text-primary-600",
  },
  {
    level: "High Pressure",
    range: "500 psi and above (up to 2,300 psi)",
    use: "Large fires in commercial and industrial spaces",
    icon: "ri-flashlight-line",
    colorClass: "bg-secondary-50 border-secondary-200/70",
    iconClass: "bg-secondary-100 text-secondary-600",
  },
];

const benefits = [
  {
    title: "Instant Activation",
    desc: "Whether the nozzles are deluge or automated, water mist systems always provide more rapid response that enables users to safely and quickly put out fires.",
    icon: "ri-timer-flash-line",
  },
  {
    title: "Effective on All Fire Types",
    desc: "Water mist systems produce very fine water droplets that can effectively penetrate into the seat of flames, extinguishing different kinds of fires and providing an increased cooling effect.",
    icon: "ri-fire-line",
  },
  {
    title: "Eco-Friendly",
    desc: "Water mist systems have no impacts to humans, pets and the environment. Regardless of where it is used to suppress fires, there are no toxicity issues, making them a safe fire protection alternative.",
    icon: "ri-leaf-line",
  },
  {
    title: "Less Water & Damage",
    desc: "The system uses much less water than sprinklers to extinguish different types of fires. Due to the small droplets of pressurized water released, there is a significantly reduced risk of water damage to your belongings after use.",
    icon: "ri-shield-check-line",
  },
];

export default function WaterMistFireProtectionPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Water Mist Fire Protection Singapore",
            description:
              "Learn how water mist fire protection systems work, their pressure levels, nozzle types, and unique benefits compared to conventional fire sprinklers in Singapore.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/2c0e0d60-a8fb-497c-b7b2-7b583ebea612_compressed_Depositphotos_207506126_l-2015.webp"
              alt="Fire sprinkler head releasing water mist and smoke suppression in action"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Water Mist Fire Protection</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Fire Suppression Systems
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Water Mist <span className="text-primary-400">Fire Protection</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Water mist fire protection is increasingly becoming a popular choice for ensuring fire safety on most commercial buildings in Singapore — the smart replacement for conventional fire sprinkler systems.
            </p>
          </div>
        </section>

        {/* How It Works */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                How It Works
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                How a Water Mist System Works
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                The system produces very fine and pressurized water sprays to contain, suppress and stop flames. When directed towards the flames, the sprays first act by cooling off the combustion and displacing the oxygen that supports burning. Finally, the droplets of water smother the heat, thereby extinguishing the fire.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                The effectiveness of water mist fire protection mainly depends on the spray characteristics including size of water droplets, pressure and water density. Systems that produce smaller droplet sizes are the best for extinguishing fires quickly. Systems that produce large droplets are mainly ideal for small and low temperature fires.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-6">
                Most water mist systems use stainless steel pipes for moving the water to the nozzles. Although the pipes are accorded anti-corrosive coating, it is always advisable to only use tap water to prevent rust. Untreated water could corrode the pipes and cause blockage of the nozzles, making the system ineffective during fire emergencies.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/60100702-105a-4df7-ac9c-0a9fe5313f97_compressed_3f6b74da-be8f-4fa3-85e5-5036754ec5be.webp"
                alt="Green EXIT sign and emergency light installed on black ceiling with staff only sign"
                title="Ezzogenics ceiling exit sign installation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Pressure Levels */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                System Variants
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Three Pressure <span className="text-primary-500">Operating Levels</span>
              </h2>
              <p className="text-foreground-600 text-base max-w-2xl mx-auto">
                Water mist systems are designed for low, medium and high pressure applications depending on the environment and fire risk level.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {pressureLevels.map((pl) => (
                <div key={pl.level} className={`rounded-2xl p-6 border ${pl.colorClass}`}>
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full mb-4 ${pl.iconClass}`}>
                    <i className={`${pl.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-1">{pl.level}</h3>
                  <p className="text-xs font-semibold text-foreground-500 mb-3">{pl.range}</p>
                  <p className="text-foreground-600 text-sm leading-relaxed">{pl.use}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nozzle Types */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Nozzle Types
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Deluge vs Automatic <span className="text-primary-500">Nozzles</span>
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto">
              When buying water mist systems, it is also important to consider the nozzles. The systems can have either deluge or automatic nozzles.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                <i className="ri-circle-line text-primary-600 text-xl"></i>
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">Deluge Nozzles</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                Deluge nozzles remain open at all times such that as soon as the water flows into the system, they start releasing it towards the flames. This provides immediate and unrestricted water discharge to the fire area.
              </p>
            </div>
            <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                <i className="ri-temp-hot-line text-accent-600 text-xl"></i>
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">Automatic Nozzles</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                Automatic nozzles are designed with a thermal element covering the head that will instantly burst under high temperatures, allowing the flow of water to extinguish the fire. They activate only where heat is detected.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Why Choose It
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Unique Benefits of Water Mist <span className="text-primary-500">Fire Protection</span>
              </h2>
              <p className="text-foreground-600 text-base max-w-2xl mx-auto">
                Compared to gaseous fire suppression agents and water sprinklers, water mist fire protection is proven to deliver the following unique benefits.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {benefits.map((b) => (
                <div key={b.title} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className={`${b.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{b.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Explore Water Mist Fire Protection Systems
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
            At Ezzogenics, we offer a huge selection of high quality fire protection systems and fire safety products for residential, commercial and industrial spaces.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Get Expert Advice
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              Browse Our Products
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}